import { useEffect, useRef, useState } from 'react'
import {
  getElementTrackingLabel,
  getGoogleAnalyticsPageTitle,
  getOutboundLinkEventName,
  isDownloadHref,
  isGoogleAnalyticsEnabled,
  trackGoogleAnalyticsEvent,
  trackGoogleAnalyticsPageView,
} from '@/lib/google-analytics'

function getCurrentPath() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function getScrollDepthPercent() {
  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
  const documentHeight = Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
  )
  if (documentHeight <= viewportHeight) return 100
  return Math.min(100, Math.round(((scrollTop + viewportHeight) / documentHeight) * 100))
}

function getClickTarget(event: MouseEvent) {
  const target = event.target instanceof Element ? event.target : null
  if (!target) return null
  return target.closest<HTMLElement>('a[href], button, [role="button"], [data-ga-event]')
}

function getSearchTermFromUrl() {
  const params = new URLSearchParams(window.location.search)
  return params.get('q')
    || params.get('search')
    || params.get('keyword')
    || params.get('lokasi')
    || ''
}

function trackClick(event: MouseEvent) {
  const target = getClickTarget(event)
  if (!target) return

  const explicitEventName = target.getAttribute('data-ga-event')
  const label = getElementTrackingLabel(target)
  const anchor = target.closest<HTMLAnchorElement>('a[href]')
  if (explicitEventName) {
    trackGoogleAnalyticsEvent(explicitEventName, {
      event_category: target.getAttribute('data-ga-category') || 'engagement',
      event_label: label,
      link_url: anchor?.href || undefined,
      page_path: getCurrentPath(),
    })
    return
  }

  if (anchor) {
    const href = anchor.href || ''
    if (!href) return
    const url = new URL(href, window.location.href)
    if (isDownloadHref(url.href) || anchor.hasAttribute('download')) {
      trackGoogleAnalyticsEvent('file_download', {
        event_category: 'file',
        event_label: label,
        link_url: url.href,
        file_name: url.pathname.split('/').pop() || '',
        page_path: getCurrentPath(),
      })
      return
    }
    if (url.origin !== window.location.origin || url.protocol === 'mailto:' || url.protocol === 'tel:') {
      trackGoogleAnalyticsEvent(getOutboundLinkEventName(url.href), {
        event_category: 'outbound',
        event_label: label,
        link_url: url.href,
        link_domain: url.hostname,
        page_path: getCurrentPath(),
      })
      return
    }
    trackGoogleAnalyticsEvent('internal_link_click', {
      event_category: 'navigation',
      event_label: label,
      link_url: `${url.pathname}${url.search}${url.hash}`,
      page_path: getCurrentPath(),
    })
    return
  }

  if (target instanceof HTMLButtonElement || target.getAttribute('role') === 'button') {
    trackGoogleAnalyticsEvent('button_click', {
      event_category: 'engagement',
      event_label: label,
      page_path: getCurrentPath(),
    })
  }
}

export function GoogleAnalyticsTracker() {
  const [routeKey, setRouteKey] = useState(() => (typeof window === 'undefined' ? '' : getCurrentPath()))
  const previousPathRef = useRef('')
  const firedScrollDepthsRef = useRef<Set<number>>(new Set())

  useEffect(() => {
    const handleRouteChange = () => setRouteKey(getCurrentPath())
    window.addEventListener('popstate', handleRouteChange)
    window.addEventListener('hashchange', handleRouteChange)
    return () => {
      window.removeEventListener('popstate', handleRouteChange)
      window.removeEventListener('hashchange', handleRouteChange)
    }
  }, [])

  useEffect(() => {
    if (!isGoogleAnalyticsEnabled()) return

    const pagePath = getCurrentPath()
    if (previousPathRef.current === pagePath) return

    firedScrollDepthsRef.current = new Set()
    trackGoogleAnalyticsPageView({
      path: pagePath,
      title: getGoogleAnalyticsPageTitle(window.location.pathname),
      location: window.location.href,
      referrer: previousPathRef.current ? `${window.location.origin}${previousPathRef.current}` : document.referrer,
    })
    const searchTerm = getSearchTermFromUrl().trim()
    if (searchTerm) {
      trackGoogleAnalyticsEvent('search', {
        event_category: 'site_search',
        search_term: searchTerm,
        page_path: pagePath,
      })
    }
    previousPathRef.current = pagePath
  }, [routeKey])

  useEffect(() => {
    if (!isGoogleAnalyticsEnabled()) return

    const handleClick = (event: MouseEvent) => trackClick(event)
    const handleScroll = () => {
      const depth = getScrollDepthPercent()
      ;[25, 50, 75, 90].forEach((threshold) => {
        if (depth >= threshold && !firedScrollDepthsRef.current.has(threshold)) {
          firedScrollDepthsRef.current.add(threshold)
          trackGoogleAnalyticsEvent('scroll_depth', {
            event_category: 'engagement',
            percent_scrolled: threshold,
            page_path: getCurrentPath(),
          })
        }
      })
    }

    document.addEventListener('click', handleClick, { capture: true })
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })
    window.setTimeout(handleScroll, 800)

    return () => {
      document.removeEventListener('click', handleClick, { capture: true })
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  return null
}
